// Copyright (c) 2025 GeoVerses. All rights reserved.
import './App.css';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import BottomNavigation from '@mui/material/BottomNavigation';
import BottomNavigationAction from '@mui/material/BottomNavigationAction';
import MapIcon from '@mui/icons-material/Map';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import EditIcon from '@mui/icons-material/Edit';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import Button from '@mui/material/Button';
import { onAuthStateChanged, signInWithPopup, signOut, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { auth, googleProvider } from './firebase';
import PoemMap from './components/PoemMap';
import PoemReader from './components/PoemReader';
import PoemEditor from './components/PoemEditor';
import MyPoems from './components/MyPoems';
import About from './components/About';
import BugReport from './components/BugReport';
import AccuracyWarningDialog from './components/AccuracyWarningDialog';
import EmailVerificationDialog from './components/EmailVerificationDialog';

const darkTheme = createTheme({
  palette: {
    mode: 'dark',
  },
});

const pages = Object.freeze({
  MAP: 'map',
  EDITOR: 'editor',
  READER: 'reader',
  MY_POEMS: 'mypoems',
  ABOUT: 'about'
});

const POEM_FETCH_INTERVAL = 60 * 1000 * 5;
const READ_RANGE_METERS = 20;

function App() {
  const [cachedLocation] = useState(() => {
    try {
      const c = localStorage.getItem('geoverses_last_location');
      return c ? JSON.parse(c) : null;
    } catch { return null; }
  });

  const [latitude, setLatitude] = useState(cachedLocation?.lat ?? 0);
  const [longitude, setLongitude] = useState(cachedLocation?.lng ?? 0);
  const [poemsToDisplay, setPoemsToDisplay] = useState([]);
  const [poemsInRange, setPoemsInRange] = useState([]);
  const [userPoemTitle, setUserPoemTitle] = useState("");
  const [userPoemBody, setUserPoemBody] = useState("");
  const [userPoemAuthor, setUserPoemAuthor] = useState("");
  const [currentPage, setCurrentPage] = useState(() => {
    return localStorage.getItem('geoverses_welcomed') ? pages.MAP : pages.ABOUT;
  });
  const [isFirstVisit] = useState(() => !localStorage.getItem('geoverses_welcomed'));
  const [zoomLevel, setZoomLevel] = useState(16);
  const [user, setUser] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);
  const [unverifiedUser, setUnverifiedUser] = useState(null);
  const [resendMessage, setResendMessage] = useState('');
  const [locationReady, setLocationReady] = useState(false);
  const [accuracyWarningOpen, setAccuracyWarningOpen] = useState(false);
  const [currentAccuracy, setCurrentAccuracy] = useState(null);
  const [bugReportOpen, setBugReportOpen] = useState(false);
  const mapRef = useRef(null);
  const latRef = useRef(latitude);
  const lngRef = useRef(longitude);
  const hasShownAccuracyWarning = useRef(false);

  const handleAccuracyWarningClose = useCallback(() => setAccuracyWarningOpen(false), []);
  const handleBugReportOpen = useCallback(() => setBugReportOpen(true), []);
  const handleBugReportClose = useCallback(() => setBugReportOpen(false), []);
  const handleAboutOpen = useCallback(() => setCurrentPage(pages.ABOUT), []);
  const handleSuccessClose = useCallback(() => {
    setSuccessOpen(false);
    setCurrentPage(pages.MAP);
  }, []);

  const handleGetStarted = () => {
    localStorage.setItem('geoverses_welcomed', 'true');
    setCurrentPage(pages.MAP);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  const handleSignIn = useCallback(() => {
    signInWithPopup(auth, googleProvider).catch((error) => console.log(error));
  }, []);

  const handleSignOut = useCallback(() => {
    signOut(auth).catch((error) => console.log(error));
  }, []);

  const handleVerifyClose = useCallback(() => {
    setVerifyOpen(false);
    setUnverifiedUser(null);
    setResendMessage('');
  }, []);

  const handleResendVerification = useCallback(async () => {
    setResendMessage('');
    try {
      if (!unverifiedUser) throw new Error('Please sign in again to resend.');
      const result = await signInWithEmailAndPassword(auth, unverifiedUser.firebaseUser.email, unverifiedUser.password);
      await sendEmailVerification(result.user);
      await signOut(auth);
      setResendMessage('Verification email sent!');
    } catch (err) {
      setResendMessage(err.message);
    }
  }, [unverifiedUser]);

  const handleEmailSignIn = useCallback(async (email, password) => {
    const result = await signInWithEmailAndPassword(auth, email, password);
    if (!result.user.emailVerified) {
      const unverifiedUserObj = result.user;
      await signOut(auth);
      setUnverifiedUser({ firebaseUser: unverifiedUserObj, password });
      setResendMessage('');
      setVerifyOpen(true);
    }
  }, []);

  const handleEmailSignUp = useCallback(async (email, password) => {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await sendEmailVerification(result.user);
    const newUser = result.user;
    await signOut(auth);
    setUnverifiedUser({ firebaseUser: newUser, password });
    setResendMessage('');
    setVerifyOpen(true);
  }, []);

  const fetchNearbyPoems = useCallback((lat, lng) => {
    const options = {
      method: "POST",
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ latitude: lat, longitude: lng })
    };
    fetch('/getLocalPoemCoords', options)
      .then((response) => response.json())
      .then((data) => {
        setPoemsToDisplay(data.poems);
        setPoemsInRange(data.poems.filter((poem) => poem.distance <= READ_RANGE_METERS));
      })
      .catch((error) => console.log(error));
  }, []);

  const handleLocationError = (err) => {
  }

  useEffect(() => {
    if (!navigator.geolocation) return;

    const onPosition = (position) => {
      const lat = position.coords.latitude;
      const lng = position.coords.longitude;
      const accuracy = position.coords.accuracy;
      
      if (accuracy > 100 && !hasShownAccuracyWarning.current) {
        setCurrentAccuracy(Math.round(accuracy));
        setAccuracyWarningOpen(true);
        hasShownAccuracyWarning.current = true;
      }
      
      setLatitude(lat);
      setLongitude(lng);
      latRef.current = lat;
      lngRef.current = lng;
      localStorage.setItem('geoverses_last_location', JSON.stringify({ lat, lng }));
      setLocationReady(prev => prev ? prev : true);
    };

    const watchId = navigator.geolocation.watchPosition(
      onPosition,
      handleLocationError,
      { enableHighAccuracy: true, maximumAge: 0, timeout: 10000 }
    );

    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  useEffect(() => {
    fetchNearbyPoems(latRef.current, lngRef.current);
    const interval = setInterval(() => {
      fetchNearbyPoems(latRef.current, lngRef.current);
    }, POEM_FETCH_INTERVAL);
    return () => clearInterval(interval);
  }, [fetchNearbyPoems]);

  const submitPoem = useCallback(async (submitEvent) => {
    submitEvent.preventDefault();
    if (!user) return;
    const idToken = await user.getIdToken();
    const options = {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${idToken}`
      },
      body: JSON.stringify({
        latitude: latRef.current,
        longitude: lngRef.current,
        title: userPoemTitle,
        author: userPoemAuthor,
        body: userPoemBody
      })
    };
    fetch('/submitPoem', options)
      .then((response) => {
        if (!response.ok) throw new Error('Submission failed');
        return response.json();
      })
      .then(() => {
        setUserPoemTitle("");
        setUserPoemAuthor("");
        setUserPoemBody("");
        fetchNearbyPoems(latRef.current, lngRef.current);
        setSuccessOpen(true);
      })
      .catch((error) => console.log(error));
  }, [userPoemTitle, userPoemAuthor, userPoemBody, user, fetchNearbyPoems]);

  const onZoomEnd = useCallback((mapEvt) => {
    setZoomLevel(mapEvt.zoom);
  }, []);

  useEffect(() => {
    if (currentPage === pages.MAP && mapRef.current) {
      mapRef.current.invalidateSize();
    }
  }, [currentPage]);

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline/>

      <EmailVerificationDialog
        open={verifyOpen}
        unverifiedUser={unverifiedUser}
        resendMessage={resendMessage}
        onResend={handleResendVerification}
        onClose={handleVerifyClose}
      />

      <AccuracyWarningDialog
        open={accuracyWarningOpen}
        accuracy={currentAccuracy}
        onClose={handleAccuracyWarningClose}
      />

      <BugReport
        open={bugReportOpen}
        onClose={handleBugReportClose}
      />
      
      <div className="App">
        <main className="App-main">
          {currentPage === pages.ABOUT &&
            <About
              onGetStarted={handleGetStarted}
              showGetStarted={isFirstVisit}
            />
          }
          {currentPage === pages.READER &&
            <PoemReader
              poemsInRange={poemsInRange}
            />
          }
          <div style={{ display: currentPage === pages.MAP ? 'block' : 'none' }}>
            <PoemMap
              latitude={latitude}
              longitude={longitude}
              hasCachedLocation={cachedLocation !== null}
              locationReady={locationReady}
              zoomLevel={zoomLevel}
              poemsToDisplay={poemsToDisplay}
              onZoomEnd={onZoomEnd}
              mapRef={mapRef}
              isVisible={currentPage === pages.MAP}
              onAboutOpen={handleAboutOpen}
              onBugReportOpen={handleBugReportOpen}
            />
          </div>
          {currentPage === pages.MY_POEMS &&
            <MyPoems
              user={user}
              onSignIn={handleSignIn}
              onEmailSignIn={handleEmailSignIn}
              onEmailSignUp={handleEmailSignUp}
              onSignOut={handleSignOut}
            />
          }
          {currentPage === pages.EDITOR &&
            <PoemEditor
              user={user}
              onSignIn={handleSignIn}
              onEmailSignIn={handleEmailSignIn}
              onEmailSignUp={handleEmailSignUp}
              onSignOut={handleSignOut}
              userPoemTitle={userPoemTitle}
              userPoemAuthor={userPoemAuthor}
              userPoemBody={userPoemBody}
              onTitleChange={(e) => setUserPoemTitle(e.target.value)}
              onAuthorChange={(e) => setUserPoemAuthor(e.target.value)}
              onBodyChange={(e) => setUserPoemBody(e.target.value)}
              onSubmitPoem={submitPoem}
              successOpen={successOpen}
              onSuccessClose={handleSuccessClose}
            />
          }
        </main>

        <BottomNavigation
          className="App-nav"
          value={currentPage}
          onChange={(event, newValue) => setCurrentPage(newValue)}
          showLabels
        >
          <BottomNavigationAction label="Read" value={pages.READER} icon={<MenuBookIcon />} />
          <BottomNavigationAction label="Map" value={pages.MAP} icon={<MapIcon />} />
          <BottomNavigationAction label="Write" value={pages.EDITOR} icon={<EditIcon />} />
          <BottomNavigationAction label="My Poems" value={pages.MY_POEMS} icon={<LibraryBooksIcon />} />
        </BottomNavigation>
      </div>
    </ThemeProvider>
  );
}

export default App;

